import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TuiGroup, TuiButton } from '@taiga-ui/core';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, finalize, skip, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { compareById } from '@core/helpers/compareById';
import { FormPickerComponent } from '@core/components';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { XAlertService } from '@core/services';
import type { RequestInstallerAssignmentInstaller } from '@outkeep/shared';
import {
  AttachExecutorsDirective,
  AttachSubdivisionsDirective,
  AttachZonesDirective,
} from '../../../request-form-common/components/request-form-common-main/directives';
import { AttachAssignmentInstallersDirective } from './attach-assignment-installers.directive';
import {
  RequestInstallerAssignmentField,
  RequestInstallerAssignmentFormControls,
  RequestInstallerAssignmentInitialValue,
} from './request-installer-assignment-form.types';

@Component({
  selector: 'x-request-installer-assignment-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    TuiButton,
    FormPickerComponent,
    AttachExecutorsDirective,
    AttachSubdivisionsDirective,
    AttachZonesDirective,
    AttachAssignmentInstallersDirective,
    TuiGroup,
  ],
  templateUrl: './request-installer-assignment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestInstallerAssignmentFormComponent implements OnInit {
  readonly $requestIds = input.required<number[]>({
    alias: 'requestIds',
  });
  readonly $ownerId = input.required<number>({
    alias: 'ownerId',
  });
  readonly $initialAssignment =
    input<RequestInstallerAssignmentInitialValue | null>(null, {
      alias: 'initialAssignment',
    });
  readonly $titleKey = input<string | null>(
    'request_form.summary.installer_assignment.title',
    {
      alias: 'titleKey',
    },
  );
  readonly $showCancel = input(true, {
    alias: 'showCancel',
  });
  readonly $completed = output<void>({
    alias: 'completedOutput',
  });
  readonly $canceled = output<void>({
    alias: 'canceledOutput',
  });

  protected readonly Field = RequestInstallerAssignmentField;

  private readonly formBuilder = inject(FormBuilder);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly alertService = inject(XAlertService);
  private readonly destroyRef = inject(DestroyRef);

  readonly formGroup =
    this.formBuilder.group<RequestInstallerAssignmentFormControls>({
      [RequestInstallerAssignmentField.ContractorCounterparty]:
        this.formBuilder.control(null, Validators.required),
      [RequestInstallerAssignmentField.ContractorSubdivision]:
        this.formBuilder.control(null, Validators.required),
      [RequestInstallerAssignmentField.InstallZone]:
        this.formBuilder.control(null),
      [RequestInstallerAssignmentField.Installer]: this.formBuilder.control(
        null,
        Validators.required,
      ),
    });
  readonly loading = this.formBuilder.control(false, {
    nonNullable: true,
  });
  readonly installerStringify = (
    installerPerson: RequestInstallerAssignmentInstaller | null,
  ) =>
    [
      installerPerson?.lastName,
      installerPerson?.firstName,
      installerPerson?.patronymic,
    ]
      .filter(Boolean)
      .join(' ');

  ngOnInit(): void {
    this.patchInitialAssignment();
    // Подписываемся после заполнения формы: сброс зависимых полей должен срабатывать
    // только при реальной смене значения, а не при повторном выборе того же
    const controls = this.formGroup.controls;

    this.resetDependentsOnChange(
      controls[RequestInstallerAssignmentField.ContractorCounterparty],
      [
        controls[RequestInstallerAssignmentField.ContractorSubdivision],
        controls[RequestInstallerAssignmentField.InstallZone],
        controls[RequestInstallerAssignmentField.Installer],
      ],
    );
    this.resetDependentsOnChange(
      controls[RequestInstallerAssignmentField.ContractorSubdivision],
      [
        controls[RequestInstallerAssignmentField.InstallZone],
        controls[RequestInstallerAssignmentField.Installer],
      ],
    );
  }

  private patchInitialAssignment(): void {
    const initialAssignment = this.$initialAssignment();

    if (!initialAssignment) {
      return;
    }

    this.formGroup.patchValue(
      {
        [RequestInstallerAssignmentField.ContractorCounterparty]:
          initialAssignment.contractorCounterparty,
        [RequestInstallerAssignmentField.ContractorSubdivision]:
          initialAssignment.contractorSubdivision,
        [RequestInstallerAssignmentField.InstallZone]:
          initialAssignment.installZone,
        [RequestInstallerAssignmentField.Installer]:
          initialAssignment.installer,
      },
      { emitEvent: false },
    );
  }

  private resetDependentsOnChange(
    control: AbstractControl<{ id: number } | null>,
    dependents: AbstractControl[],
  ): void {
    control.valueChanges
      .pipe(
        startWith(control.value),
        distinctUntilChanged(compareById),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => dependents.forEach(dependent => dependent.reset()));
  }

  submit(): void {
    if (this.formGroup.invalid || this.loading.value) {
      this.formGroup.markAllAsTouched();

      return;
    }

    const value = this.formGroup.getRawValue();

    this.loading.setValue(true);
    this.requestsApiService
      .assignInstallerToRequests$({
        requestIds: this.$requestIds(),
        contractorCounterpartyId:
          value[RequestInstallerAssignmentField.ContractorCounterparty]!.id,
        contractorCounterpartySubdivisionId:
          value[RequestInstallerAssignmentField.ContractorSubdivision]!.id,
        installZoneId:
          value[RequestInstallerAssignmentField.InstallZone]?.id ?? null,
        installerId: value[RequestInstallerAssignmentField.Installer]!.id,
      })
      .pipe(finalize(() => this.loading.setValue(false)))
      .subscribe({
        next: () => this.$completed.emit(),
        error: error =>
          this.alertService.error(error?.error?.message ?? 'Error'),
      });
  }

  clearInstaller(): void {
    if (this.loading.value) {
      return;
    }

    this.loading.setValue(true);
    this.requestsApiService
      .clearInstallerFromRequests$({ requestIds: this.$requestIds() })
      .pipe(finalize(() => this.loading.setValue(false)))
      .subscribe({
        next: () => this.$completed.emit(),
        error: error =>
          this.alertService.error(error?.error?.message ?? 'Error'),
      });
  }

  cancel(): void {
    this.$canceled.emit();
  }
}

export { RequestInstallerAssignmentField } from './request-installer-assignment-form.types';
